/**
 * fetchWithNetworkRetry — wraps `fetch` with retry + exponential backoff for
 * transient network-layer failures (no response received).
 *
 * What this retries:
 *   - TypeError whose message matches a known cross-browser "no response" signal:
 *     - Chrome:  "Failed to fetch"
 *     - Firefox: "NetworkError when attempting to fetch resource"
 *     - Safari:  "The network connection was lost." /
 *                "The Internet connection appears to be offline."
 *
 * What this does NOT retry:
 *   - HTTP responses with non-2xx status codes. The server responded; the caller
 *     decides what to do. Retrying a server-sent 4xx/5xx is almost always wrong
 *     (it's either a client bug or the server is already handling load).
 *   - Non-TypeError errors, or TypeErrors unrelated to fetch.
 *
 * Safety note on idempotency:
 *   A `TypeError: Failed to fetch` means the client received no response. The
 *   server may or may not have processed the request. For safe retrying, only
 *   call this helper for operations that are idempotent — typically GETs. Do
 *   not use this for POST/PATCH/DELETE without a server-side idempotency key,
 *   or retrying could cause duplicate side effects.
 *
 * Pattern originally implemented in `TakeoffGenerationApi.ts`; extracted here
 * so other API call sites can opt in (ENG-10006).
 */

import { logger } from "utilities/logger";

export interface NetworkRetryOptions {
  /** Number of retries after the initial attempt. Default: 5. */
  maxRetries?: number;
  /** Base backoff in ms before the first retry (doubles each attempt). Default: 2000. */
  baseBackoffMs?: number;
  /** Upper bound on the backoff delay. Default: 30000. */
  maxBackoffMs?: number;
  /** Label used in log messages to identify the call site. */
  label?: string;
}

const DEFAULT_MAX_RETRIES = 5;
const DEFAULT_BASE_BACKOFF_MS = 2_000;
const DEFAULT_MAX_BACKOFF_MS = 30_000;

/**
 * Cross-browser detection for network-layer fetch failures (no response received).
 */
export function isTransientNetworkError(error: unknown): boolean {
  if (!(error instanceof TypeError)) {
    return false;
  }
  const message = error.message;
  // Match the exact browser-emitted strings to avoid false positives from
  // other APIs (WebRTC/WebSocket/IndexedDB TypeErrors can contain "NetworkError"
  // in their messages). The Safari variants are phrased as sentences, so a
  // substring match on a distinctive fragment is used.
  return (
    message === "Failed to fetch" ||
    message === "NetworkError when attempting to fetch resource" ||
    message.includes("network connection was lost") ||
    message.includes("Internet connection appears to be offline")
  );
}

/**
 * Drop-in replacement for `fetch` that retries transient network failures.
 */
export async function fetchWithNetworkRetry(
  input: RequestInfo | URL,
  init?: RequestInit,
  opts: NetworkRetryOptions = {}
): Promise<Response> {
  const maxRetries = opts.maxRetries ?? DEFAULT_MAX_RETRIES;
  const baseBackoffMs = opts.baseBackoffMs ?? DEFAULT_BASE_BACKOFF_MS;
  const maxBackoffMs = opts.maxBackoffMs ?? DEFAULT_MAX_BACKOFF_MS;
  const label = opts.label ?? "fetchWithNetworkRetry";

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    if (attempt > 0) {
      const backoffMs = Math.min(
        baseBackoffMs * Math.pow(2, attempt - 1),
        maxBackoffMs
      );
      logger.warn(
        `[${label}] transient network error, retry ${attempt}/${maxRetries} in ${backoffMs}ms`
      );
      await new Promise<void>((resolve) => setTimeout(resolve, backoffMs));
    }

    try {
      return await fetch(input, init);
    } catch (error) {
      if (isTransientNetworkError(error) && attempt < maxRetries) {
        continue;
      }
      if (isTransientNetworkError(error)) {
        logger.error(
          `[${label}] transient network error — giving up after ${maxRetries} retries`
        );
      }
      throw error;
    }
  }

  // Unreachable — the loop either returns a Response or throws. Kept as a
  // safety net for TypeScript's control-flow analysis and in case a future
  // refactor breaks the invariant.
  throw new Error(`[${label}] request failed after retries`);
}
